import React, { ReactElement, useContext, ReactNode } from "react";
import { Form, Formik, FormikConfig, FormikErrors, FormikValues } from "formik";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";

import CloseIcon from "@mui/icons-material/Close";

import { FormikStepProps } from "modules/Forms/components/formikStep.component";
import { stepContext } from "modules/Forms/components/formikStepperProvider.component";
import { PSL } from "modules/PromoItems/domains/psl.domain";

export enum StepperDirection {
  Left,
  Right,
}

export enum FormikStepperLastStepConfirmation {
  YesNo,
  OkCancel,
}

export type FormikFormLastStepConfirmation = {
  text: string;
  title: string;
  type: FormikStepperLastStepConfirmation;
};

export interface FormikStepperCustomProps {
  title?: string;
  footer?: any;
  onClose?: () => void;
  showStepper?: boolean;
  submitButtons?: any;
  lastStepConfirmation?: FormikFormLastStepConfirmation;
  setEnableButtonColor?: boolean;
  children?: React.ReactNode | React.ReactNode[];
}

export type FormikFormProps = FormikConfig<FormikValues> & FormikStepperCustomProps;

export function FormikStepper({
  children,
  title,
  onClose,
  submitButtons,
  showStepper,
  setEnableButtonColor,
  onSubmit,
  ...restFormikProps
}: FormikFormProps) {
  const childrenArray = React.Children.toArray(
    children
  ) as ReactElement<FormikStepProps>[];
  const [loading, setLoading] = React.useState(false);
  const [lastStepConfirmationOpen, setLastStepConfirmationOpen] =
    React.useState(false);
  const [confirmedAfterLastStep, setConfirmedAfterLastStep] =
    React.useState(false);
  const [nextStepConfirmationOpen, setNextStepConfirmationOpen] =
    React.useState(false);

  const lastStepConfirmation = {
    text: "Quality Documents evidences are required for some Materials",
    title: "Are you sure to continue?",
    type: FormikStepperLastStepConfirmation.OkCancel,
  };

  const {
    currentStep,
    setCurrentStep,
    displayedCurrentStep,
    setDisplayedCurrentStep,
    setDirection,
    currentFormikValue,
    setCurrentFormikValue,
  } = useContext(stepContext);

  const currentChild = childrenArray[currentStep];

  function isLastStep() {
    return currentStep === childrenArray.length - 1;
  }

  function isChildPsl(vendor: any) {
    const psl = new PSL(vendor);
    return psl.isChildPsl();
  }

  return (
    <Formik
      {...restFormikProps}
      {...(currentChild.props.validationSchema
        ? { validationSchema: currentChild.props.validationSchema }
        : {})}
      {...(currentChild.props.getValidationSchema
        ? {
            validationSchema:
              currentChild.props.getValidationSchema(currentFormikValue),
          }
        : {})}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={async (values, helpers) => {
        if (!loading) {
          if (isLastStep()) {
            const qualityConfirmationNeeded = values.materials.some(
              (material: any) => {
                if (!material.promoCategoryQualityRequired) {
                  return false;
                }
                const hasQualityFile = material.mediaFiles.some(
                  (media: any) => media.isQuality
                );
                return material.promoCategoryQualityRequired && !hasQualityFile;
              }
            );
            if (
              !isChildPsl(values.vendor) &&
              qualityConfirmationNeeded &&
              !confirmedAfterLastStep
            ) {
              setLastStepConfirmationOpen(true);
            } else {
              setLoading(true);
              try {
                await onSubmit(values, helpers);
              } catch (e) {
                setLoading(false);
              }
              setLoading(false);
              setLastStepConfirmationOpen(false);
            }
          } else {
            setCurrentFormikValue(values);
            setDirection(StepperDirection.Right);
            setCurrentStep(currentStep + 1);
            setDisplayedCurrentStep(displayedCurrentStep + 1);
          }
        }
      }}
    >
      {(formikContext) => {
        const { submitForm, setFieldValue, errors, values, dirty } =
          formikContext;

        return (
          <>
            <Form autoComplete="off">
              <Dialog
                onClose={() => setNextStepConfirmationOpen(false)}
                open={nextStepConfirmationOpen}
                // width={600}
                // askForConfirmationAtClose={false}
              >
                <div>Are you sure?</div>
                <div>
                  {currentChild.props.confirmationOnNext &&
                    (() => {
                      const result =
                        currentChild.props.confirmationOnNext(values);
                      return result === false ? null : result;
                    })()}
                </div>
                <div>
                  <div>
                    <div>
                      <Button
                        color="primary"
                        fullWidth
                        disabled={loading}
                        type="submit"
                        onClick={async () => {
                          setNextStepConfirmationOpen(false);
                          await submitForm();
                        }}
                      >
                        Go to next step
                      </Button>
                    </div>
                    <div>
                      <Button
                        color="secondary"
                        fullWidth
                        disabled={loading}
                        onClick={() => {
                          setNextStepConfirmationOpen(false);
                        }}
                      >
                        Stay on this step
                      </Button>
                    </div>
                  </div>
                </div>
              </Dialog>
              <Dialog onClose={() => {}} open={lastStepConfirmationOpen}>
                <DialogTitle>{lastStepConfirmation.title}</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    {lastStepConfirmation.text}
                  </DialogContentText>
                </DialogContent>

                <DialogActions>
                  <Button
                    color="primary"
                    fullWidth
                    disabled={loading}
                    type="submit"
                    onClick={async () => {
                      setConfirmedAfterLastStep(true);
                      await submitForm();
                    }}
                  >
                    {loading && <CircularProgress size={18} />}
                    {!loading &&
                      (lastStepConfirmation.type ===
                      FormikStepperLastStepConfirmation.OkCancel
                        ? "OK"
                        : lastStepConfirmation.type ===
                          FormikStepperLastStepConfirmation.YesNo
                        ? "YES"
                        : "Save")}
                  </Button>
                  <Button
                    color="warning"
                    fullWidth
                    disabled={loading}
                    onClick={() => {
                      setConfirmedAfterLastStep(false);
                      setLastStepConfirmationOpen(false);
                    }}
                  >
                    {lastStepConfirmation.type ===
                    FormikStepperLastStepConfirmation.OkCancel
                      ? "Cancel"
                      : lastStepConfirmation.type ===
                        FormikStepperLastStepConfirmation.YesNo
                      ? "No"
                      : "Cancel"}
                  </Button>
                </DialogActions>
              </Dialog>

              {title && <Typography variant="h4">{title}</Typography>}
              {currentChild}
              <Grid
                container
                spacing={2}
                justifyContent={"space-between"}
                alignItems={"flex-start"}
                sx={{ mt: 4 }}
              >
                <Grid item>
                  <Button
                    type="button"
                    variant="outlined"
                    size="large"
                    disabled={false}
                    onClick={() => {
                      if (currentStep === 0) {
                        if (onClose)
                          onClose();
                      } else {
                        setDirection(StepperDirection.Left);
                        setCurrentStep(currentStep - 1);
                        setDisplayedCurrentStep(displayedCurrentStep - 1);
                      }
                    }}
                  >
                    Back
                  </Button>
                </Grid>
                {showStepper && childrenArray.length > 1 && (
                  <Grid item>
                    <Stepper alternativeLabel activeStep={currentStep}>
                      {childrenArray.map((child: any, index: number) => {
                        return (
                          <Step key={index} completed={currentStep > index}>
                            <StepButton
                              onClick={() => {
                                setCurrentStep(index);
                                setDisplayedCurrentStep(index + 1);
                              }}
                            >
                              {child.props.title || `Step ${index + 1}`}
                            </StepButton>
                          </Step>
                        );
                      })}
                    </Stepper>
                  </Grid>
                )}
                <Grid item>
                  {!isLastStep() ? (
                    <>
                      {currentChild.props.nextStepButtons ? (
                        currentChild.props.nextStepButtons(
                          submitForm,
                          values,
                          setFieldValue
                        )
                      ) : (
                        <Button
                          type="button"
                          variant="contained"
                          size="large"
                          disabled={!dirty && !formikContext.isValid}
                          onClick={(e) => {
                            e.preventDefault();
                            if (
                              currentChild.props.confirmationOnNext &&
                              currentChild.props.confirmationOnNext(values) !==
                                false
                            ) {
                              setNextStepConfirmationOpen(true);
                            } else {
                              submitForm();
                            }
                          }}
                        >
                          Next
                        </Button>
                      )}
                    </>
                  ) : submitButtons ? (
                    submitButtons(
                      submitForm,
                      setFieldValue,
                      loading,
                      errors,
                      values,
                      formikContext
                    )
                  ) : (
                    <Button
                      variant="contained"
                      size="large"
                      disabled={
                        loading ||
                        (!formikContext.dirty && !formikContext.isValid)
                      }
                      type="submit"
                      startIcon={
                        loading ? <CircularProgress size={14} /> : null
                      }
                    >
                      {loading ? "Submitting" : "Submit"}
                    </Button>
                  )}
                </Grid>
              </Grid>
              {restFormikProps.footer &&
                !isLastStep() &&
                currentStep !== childrenArray.length - 2 &&
                restFormikProps.footer(formikContext)}
            </Form>
          </>
        );
      }}
    </Formik>
  );
}

export default FormikStepper;
